import { Fragment, useEffect, useRef, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";

import { MESSAGE_CENTER_FAQ_ITEM_ID } from "@/constants";
import {
  DayDivider,
  EventMessage,
  Message,
  NewMessageComposer,
} from "@/domains/messages/components";
import { DEFAULT_COMPOSER_HEIGHT } from "@/domains/messages/models/constants";
import { useSendMessage } from "@/domains/messages/mutations/use-send-message";
import { useMessages } from "@/domains/messages/queries/use-messages";
import { useProfile } from "@/domains/profile/queries/use-profile";
import { takeCareTeamMessageTopic } from "@/domains/progress/utils/care-team-message";
import { useBreakpoint } from "@/hooks/use-breakpoint";
import { textLink } from "@/ui";

const buildDefaultMessage = (topic: string | null) => {
  return topic === null || topic.trim().length === 0
    ? undefined
    : `I'd like to talk about "${topic.trim()}".`;
};

const Messages = () => {
  /* Read once: the helper clears the topic, and StrictMode's second render would find it gone. */
  const defaultMessageRef = useRef<string | undefined>(undefined);
  const hasReadTopicRef = useRef(false);

  if (!hasReadTopicRef.current) {
    hasReadTopicRef.current = true;
    defaultMessageRef.current = buildDefaultMessage(takeCareTeamMessageTopic());
  }

  const [composerHeight, setComposerHeight] = useState(DEFAULT_COMPOSER_HEIGHT);
  const messagesQuery = useMessages();
  const sendMessageMutation = useSendMessage();
  const profileQuery = useProfile();
  const isDesktop = useBreakpoint("sm");

  useEffect(() => {
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  }, [messagesQuery.data]);

  if (!messagesQuery.data) {
    return null;
  }

  const handleSendMessage = (newMessage: string) => {
    sendMessageMutation.mutate({
      content: newMessage,
      threadId: messagesQuery.data.threadId,
    });
  };

  return (
    <main className="flex flex-1 flex-col bg-canvas">
      <section className="flex size-full max-w-4xl flex-col justify-end self-center px-4 pt-8 pb-3 lg:px-0">
        {!messagesQuery.data.hasMessages && (
          <section className="flex min-h-[60vh] flex-col items-center justify-center gap-3 px-6 text-center">
            <p className="text-xl font-semibold text-content">{`You don't have any messages yet.`}</p>

            <p className="text-content-secondary">Start a conversation with your clinician.</p>
          </section>
        )}

        {messagesQuery.data.hasMessages ? (
          <>
            <section className="mx-auto max-w-2xl text-center leading-relaxed font-medium text-balance text-content-secondary">
              {`We can't wait to help. But before you reach out, please take a moment `}
              <br className="hidden sm:block" />
              to learn how our{" "}
              <Link
                className={textLink}
                params={{ ...(isDesktop ? {} : { id: `${MESSAGE_CENTER_FAQ_ITEM_ID}` }) }}
                search={{ ...(isDesktop ? { id: `${MESSAGE_CENTER_FAQ_ITEM_ID}` } : {}) }}
                to={isDesktop ? "/faqs" : "/faqs/$id"}
              >
                message center
              </Link>{" "}
              works.
            </section>
            <section className="flex flex-col gap-4 pt-8" style={{ paddingBottom: composerHeight }}>
              {Object.keys(messagesQuery.data.messagesByDay).map((day, index) => {
                return (
                  <Fragment key={index}>
                    <DayDivider date={day} />

                    {messagesQuery.data.messagesByDay[day].map((message, index) => {
                      if (message.isEvent) {
                        return <EventMessage content={message.content} key={index} />;
                      }

                      return (
                        <Message.Root key={index} message={message}>
                          <Message.Avatar placeholderInitials={profileQuery.data?.initials} />

                          <Message.Bubble time />
                        </Message.Root>
                      );
                    })}
                  </Fragment>
                );
              })}
            </section>
          </>
        ) : null}

        <NewMessageComposer
          defaultMessage={defaultMessageRef.current}
          onResize={setComposerHeight}
          onSend={handleSendMessage}
        />
      </section>
    </main>
  );
};

export const Route = createFileRoute("/_private/messages/")({
  component: Messages,
});
